import { api } from "../scripts/api.js";

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const fmtDate = (v) => v
   ? new Date(v).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
   }) : "-";

const fmtWhen = (v, fallback = "-") => v
   ? new Date(v).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
   })
   : fallback;

class AppScores extends HTMLElement {
   connectedCallback() {
      this.classes = [];
      this.selectedId = null;

      this.innerHTML = `
         <section>
            <div class="score-head">
               <h1>My Scores</h1>
            </div>

            <div class="classpick" data-pick hidden>
               <button type="button" class="classpick-trigger" data-trigger aria-haspopup="listbox" aria-expanded="false">
                  <span class="classpick-mark" aria-hidden="true"></span>
                  <span class="classpick-text">
                     <span class="classpick-name" data-name></span>
                     <span class="classpick-meta" data-meta></span>
                  </span>
                  <span class="classpick-caret" aria-hidden="true"></span>
               </button>
               <ul class="classpick-list" data-list role="listbox" tabindex="-1" hidden></ul>
            </div>

            <div class="table-score" data-body aria-live="polite">
            </div>
         </section>
      `;

      this.pick = this.querySelector("[data-pick]");
      this.trigger = this.querySelector("[data-trigger]");
      this.list = this.querySelector("[data-list]");
      this.nameEl = this.querySelector("[data-name]");
      this.metaEl = this.querySelector("[data-meta]");
      this.body = this.querySelector("[data-body]");
      this.mark = this.querySelector(".classpick-mark");

      this.onDocClick = (e) => {
         if (!this.pick.contains(e.target)) this.close();
      }
      this.onPop = () => this.load(new URL(location.href).searchParams.get("class"));

      document.addEventListener("click", this.onDocClick);
      window.addEventListener("popstate", this.onPop);
      this.trigger.addEventListener("click", () => this.toggle());
      this.list.addEventListener("click", (e) => {
         const li = e.target.closest("[data-id]");
         if (li) this.select(li.dataset.id);
      });

      this.boot();
   }

   disconnectedCallback() {
      document.removeEventListener("click", this.onDocClick);
      window.removeEventListener("popstate", this.onPop);
   }

   async boot() {
      this.body.innerHTML = `<p class="score-note">Loading your classes...</p>`;
      try {
         const res = await api("/me");
         if (!res.ok) throw new Error(`/me ${res.status}`);
         const { classes } = await res.json();
         this.classes = (classes || [])
            .filter((c) => c.role_in_class === "student")
            .map((c) => ({
               id: c.class_id,
               name: c.title ? `${c.course_code} - ${c.title}` : c.course_code,
               section: c.section,
               term: c.term,   
               active: c.active !== false,
            }));
      } catch (err) {
         this.body.innerHTML = `<p class="score-note">Couldn't load your classes. Refresh to try again.</p>`;
         return;
      }
      this.pick.hidden = false;
      this.renderList();
      if (!this.classes.length) {
         this.mark.hidden = true;
         this.nameEl.textContent = "No classes";
         this.metaEl.textContent = "";
         this.body.innerHTML = `<p class="score-note">You're not enrolled in a class yet.</p>`;
         return;
      }
      this.load(new URL(location.href).searchParams.get("class"));
   }

   /* ---------- class picker ---------- */
   renderList() {
      if (!this.classes.length) {
         this.list.innerHTML = `<li class="classpick-empty" aria-disabled="true">No classes</li>`;
         return;
      }

      this.list.innerHTML = this.classes.map((c, i) => `
         <li class="classpick-option" role="option" data-id="${esc(c.id)}"
            aria-selected="${c.id === this.selectedId}">
            <span class="classpick-name">${esc(c.name)}</span>
            <span class="classpick-meta">${esc(c.section)} · ${esc(c.term)}</span>
         </li>
      `).join("");
   }

   toggle() {
      this.list.hidden ? this.open() : this.close();
   }

   open() {
      this.list.hidden = false;
      this.trigger.setAttribute("aria-expanded", "true");
      
   }

   close() {
      if (this.list.hidden) return;
      this.list.hidden = true;
      this.trigger.setAttribute("aria-expanded", "false");
   }

   select(id) {
      this.close();
      const next = Number(id);
      if (next === this.selectedId) return;
      const url = new URL(location.href);
      url.searchParams.set("class", next);
      history.pushState({}, "", url);
      this.load(next);
   }

   /* ---------- scores ---------- */
   async load(classId) {
      const wanted = Number(classId);
      const cls = this.classes.find((c) => c.id === wanted) || this.classes[0];
      if (!cls) return;
      this.selectedId = cls.id;
      this.mark.hidden = false;
      this.mark.classList.toggle("is-off", !cls.active);
      this.nameEl.textContent = cls.name;
      this.metaEl.textContent = `${cls.section} · ${cls.term}`;
      this.renderList();

      this.body.innerHTML = `<p class="score-note">Loading scores...</p>`;
      try {
         const res = await api(`/me/classes/${encodeURIComponent(cls.id)}/assignments`);
         if (!res.ok) throw new Error(`assignments ${res.status}`);
         const data = await res.json();
         this.renderScores(data);
      } catch (err) {
         this.body.innerHTML = `<p class="score-note">Couldn't load scores for this class. Refresh to try again.</p>`;
      }
   }

   renderScores({ assignments }) {
      if (!assignments.length) {
         this.body.innerHTML = `<p class="score-note">No assignments posted yet. Scores appear here as soon as your instructor publishes one.</p>`;
         return;
      }

      this.body.innerHTML = `
         <table class="score-table">
            <colgroup>
               <col style="width:40%">
               <col style="width:22%">
               <col style="width:22%">
               <col style="width:16%">
            </colgroup>
            <thead>
               <tr>
                  <th scope="col">Assignment</th>
                  <th scope="col">Due Date</th>
                  <th scope="col">Last Activity</th>
                  <th scope="col" class="num">Score</th>
               </tr>
            </thead>
            <tbody>
               ${assignments.map((a) => this.row(a)).join("")}
            </tbody>
         </table>
      `;
   }

   row(a) {
      const score = a.best_effective === null ? "-" : `${Math.round(Number(a.best_effective) * 100)}%`;
      return `
         <tr class="score-row">
            <td class="score-name">${esc(a.title)}</td>
            <td>${esc(fmtDate(a.due_at))}</td>
            <td class="score-when">${esc(fmtWhen(a.last_activity, "No activity yet"))}</td>
            <td class="num">${score}</td>
         </tr>`;
   }
}

customElements.define("app-scores", AppScores);